import { createReducer, on } from '@ngrx/store';
import {
  Company,
  CompanyListItem,
  PagedResult,
  SalesforceCompany,
} from '../../core';
import {
  CompaniesPageActions,
  CompaniesAPIActions,
  CompanyDetailPageActions,
  CompanyDetailAPIActions,
  CompanyFormActions,
  CompanyFormAPIActions,
} from './companies.actions';

export interface CompaniesState {
  // List state
  companies: PagedResult<CompanyListItem> | null;
  loading: boolean;
  error: string | null;
  currentPage: number;
  pageSize: number;
  searchTerm: string;
  sortBy: string;
  sortOrder: 'asc' | 'desc' | '';

  // Detail state
  currentCompany: Company | null;
  currentCompanyLoading: boolean;
  currentCompanyError: string | null;

  // Form state
  saving: boolean;
  saveSuccess: boolean;
  saveError: string | null;
  arNumberExists: boolean | null;
  salesforceResults: SalesforceCompany[];
  salesforceLoading: boolean;
}

export const initialState: CompaniesState = {
  companies: null,
  loading: false,
  error: null,
  currentPage: 1,
  pageSize: 25,
  searchTerm: '',
  sortBy: 'arNumber',
  sortOrder: 'asc',
  currentCompany: null,
  currentCompanyLoading: false,
  currentCompanyError: null,
  saving: false,
  saveSuccess: false,
  saveError: null,
  arNumberExists: null,
  salesforceResults: [],
  salesforceLoading: false,
};

export const companiesReducer = createReducer(
  initialState,

  // Load Companies
  on(CompaniesPageActions.loadCompanies, (state, { page, pageSize }) => ({
    ...state,
    loading: true,
    error: null,
    currentPage: page ?? state.currentPage,
    pageSize: pageSize ?? state.pageSize,
  })),

  on(CompaniesPageActions.changePage, (state, { page }) => ({
    ...state,
    loading: true,
    currentPage: page,
  })),

  on(CompaniesPageActions.changePageSize, (state, { pageSize }) => ({
    ...state,
    loading: true,
    pageSize,
    currentPage: 1,
  })),

  on(CompaniesPageActions.searchCompanies, (state, { search }) => ({
    ...state,
    loading: true,
    searchTerm: search,
    currentPage: 1,
  })),

  on(CompaniesPageActions.sort, (state, { field }) => {
    const currentSortBy = state.sortBy;
    const newSortBy = field;
    let newSortOrder: 'asc' | 'desc' = 'asc';

    if (currentSortBy === field) {
      newSortOrder = state.sortOrder === 'asc' ? 'desc' : 'asc';
    }

    return {
      ...state,
      loading: true,
      sortBy: newSortBy,
      sortOrder: newSortOrder,
      currentPage: 1,
    };
  }),

  on(CompaniesAPIActions.loadCompaniesSuccess, (state, { result }) => ({
    ...state,
    companies: result,
    loading: false,
    error: null,
  })),

  on(CompaniesAPIActions.loadCompaniesFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),

  // Company Detail
  on(CompanyDetailPageActions.loadCompany, state => ({
    ...state,
    currentCompany: null,
    currentCompanyLoading: true,
    currentCompanyError: null,
  })),

  on(CompanyDetailAPIActions.loadCompanySuccess, (state, { company }) => ({
    ...state,
    currentCompany: company,
    currentCompanyLoading: false,
  })),

  on(CompanyDetailAPIActions.loadCompanyFailure, (state, { error }) => ({
    ...state,
    currentCompanyLoading: false,
    currentCompanyError: error,
  })),

  on(CompanyDetailAPIActions.loadCompanyNotFound, (state, { arNumber }) => ({
    ...state,
    currentCompanyLoading: false,
    currentCompanyError: `Company ${arNumber} not found`,
  })),

  on(CompanyDetailPageActions.clearCurrentCompany, state => ({
    ...state,
    currentCompany: null,
    currentCompanyError: null,
  })),

  // Create Company
  on(CompanyFormActions.createCompany, state => ({
    ...state,
    saving: true,
    saveSuccess: false,
    saveError: null,
  })),

  on(CompanyFormAPIActions.createCompanySuccess, state => ({
    ...state,
    saving: false,
    saveSuccess: true,
  })),

  on(CompanyFormAPIActions.createCompanyFailure, (state, { error }) => ({
    ...state,
    saving: false,
    saveSuccess: false,
    saveError: error,
  })),

  // Update Company
  on(CompanyFormActions.updateCompany, state => ({
    ...state,
    saving: true,
    saveSuccess: false,
    saveError: null,
  })),

  on(CompanyFormAPIActions.updateCompanySuccess, (state, { company }) => ({
    ...state,
    saving: false,
    saveSuccess: true,
    currentCompany: company,
  })),

  on(CompanyFormAPIActions.updateCompanyFailure, (state, { error }) => ({
    ...state,
    saving: false,
    saveSuccess: false,
    saveError: error,
  })),

  // AR Number check
  on(CompanyFormActions.checkARNumberExists, state => ({
    ...state,
    arNumberExists: null,
  })),

  on(CompanyFormAPIActions.aRNumberExists, (state, { exists }) => ({
    ...state,
    arNumberExists: exists,
  })),

  // Salesforce search
  on(CompanyFormActions.searchSalesforce, state => ({
    ...state,
    salesforceLoading: true,
    salesforceResults: [],
  })),

  on(CompanyFormAPIActions.salesforceSearchSuccess, (state, { results }) => ({
    ...state,
    salesforceLoading: false,
    salesforceResults: results,
  })),

  on(CompanyFormAPIActions.salesforceSearchFailure, state => ({
    ...state,
    salesforceLoading: false,
  }))
);
